import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatSEK, rangePreset } from "@/lib/utils-app";
import {
  TrendUp, TrendDown, Bank, Plus, Minus, Equals, DownloadSimple, CalendarBlank,
} from "@phosphor-icons/react";
import DateRangeBar from "@/pages/DateRangeBar";
import { useData } from "@/lib/dataStore";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const RANGE_OPTIONS = [
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "Year to Date" },
  { value: "all", label: "All Time" },
];

export default function ProfitPage() {
  const { sales, expenses, settings } = useData();
  const entity = settings?.business_entity_type || "EF";
  const [range, setRange] = useState({ preset: "year", ...rangePreset("year") });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportPreset, setExportPreset] = useState("month");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    setLoading(true);
    api.get("/analytics/profit", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [range, sales.length, expenses.length, entity]);

  const periodLabel = useMemo(() => {
    const p = RANGE_OPTIONS.find((r) => r.value === range.preset)?.label || "Custom";
    if (!range.start && !range.end) return p;
    return `${p} · ${range.start || ""} → ${range.end || "today"}`;
  }, [range]);

  const exportPnL = async () => {
    setExporting(true);
    try {
      const rng = rangePreset(exportPreset);
      const params = {};
      if (rng.start) params.start = rng.start;
      if (rng.end) params.end = rng.end;
      const { data: d } = await api.get("/analytics/profit", { params });

      // Build ledger rows for the worksheet
      const rows = [];
      rows.push(["The NordHim Ledger — Profit & Loss Statement"]);
      rows.push([`Period: ${RANGE_OPTIONS.find((o) => o.value === exportPreset)?.label}  ·  ${rng.start || "All"} → ${rng.end || "today"}`]);
      rows.push([]);
      rows.push(["Line Item", "Type", "Count", "Amount (SEK)"]);
      rows.push(["REVENUE", "", "", ""]);
      (d.revenue_lines || []).forEach((r) =>
        rows.push([`  ${r.label}`, "Addition (+)", r.count, r.net_sek])
      );
      rows.push(["Net Revenue (Excl. Moms)", "Subtotal", d.sales_count, d.net_revenue]);
      rows.push([]);
      rows.push(["EXPENDITURE (Net of VAT — input Moms reclaimable)", "", "", ""]);
      (d.expense_lines || []).forEach((e) =>
        rows.push([`  ${e.label}`, "Deduction (−)", e.count, e.amount_net_sek ?? e.amount_sek])
      );
      rows.push(["Total Expenditure (Net)", "Subtotal", d.expenses_count, d.total_expenditure_net ?? d.total_expenditure]);
      rows.push([]);
      rows.push(["Net Profit Before Tax", "Net Revenue − Net Expenditure", "", d.net_profit_before_tax]);
      rows.push([]);
      const isEF = (d.business_entity_type || "EF") === "EF";
      if (isEF) {
        const ef = d.ef || {};
        rows.push(["PERSONAL INCOME TAX (Enskild Firma)", "", "", ""]);
        rows.push([`  Egenavgifter @ ${((ef.egenavgifter_rate || 0.2897) * 100).toFixed(2)}%`, "Deduction (−)", "", ef.egenavgifter || 0]);
        rows.push([`  Inkomstskatt @ ${((ef.municipal_tax_rate || 0.32) * 100).toFixed(0)}%`, "Deduction (−)", "", ef.inkomstskatt || 0]);
        rows.push(["  Total personal tax", "Subtotal", "", ef.total_tax || 0]);
        rows.push([]);
        rows.push(["Personal Income After Tax", "Final", "", ef.personal_income_after_tax || 0]);
        rows.push(["  Suggested reserve (40 %)", "Memo", "", ef.tax_reserve_low || 0]);
        rows.push(["  Suggested reserve (45 %)", "Memo", "", ef.tax_reserve_high || 0]);
      } else {
        const ab = d.ab || {};
        rows.push(["CORPORATE TAX (Bolagsskatt — Aktiebolag)", "", "", ""]);
        rows.push([`  Bolagsskatt @ ${((ab.bolagsskatt_rate || 0.206) * 100).toFixed(1)}%`, "Deduction (−)", "", ab.bolagsskatt ?? d.corporate_tax_20_6]);
        rows.push([]);
        rows.push(["Net Capital inside AB", "Final", "", ab.net_capital_inside_ab ?? d.net_profit_after_tax_20_6]);
      }
      rows.push([]);
      rows.push(["MEMO — VAT (Moms) reconciliation", "", "", ""]);
      rows.push(["  Output Moms (sales VAT collected)", "Memo", "", d.output_moms ?? d.moms_total_owed]);
      rows.push(["  Input Moms (expense VAT paid)", "Memo", "", d.input_moms || 0]);
      rows.push(["  Net VAT (Output − Input)", "Memo", "", d.net_vat_to_pay ?? d.moms_total_owed]);
      rows.push(["  Gross Revenue (Incl. Moms)", "Memo", "", d.gross_revenue]);

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [{ wch: 42 }, { wch: 28 }, { wch: 8 }, { wch: 16 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "P&L Statement");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const presetLabel = exportPreset === "year" ? "YTD" : exportPreset.toUpperCase();
      const filename = `TNL_PnL_${presetLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`;

      // Web Share API on mobile, download elsewhere
      const file = new File([blob], filename, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "TNL Profit & Loss" });
          toast.success("Shared — pick Files to save to iCloud");
        } catch (e) {
          if (e?.name !== "AbortError") throw e;
        }
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
        toast.success(`Downloaded ${filename}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Tab 4</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">Profit &amp; Loss</h1>
        <p className="text-sm text-slate-500 mt-1">Ledger-style statement · {periodLabel}</p>
      </div>

      <DateRangeBar range={range} setRange={setRange} />

      {/* Export bar */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <CalendarBlank size={18} weight="duotone" className="text-emerald-800" />
              <span className="font-semibold">Export P&amp;L for:</span>
            </div>
            <Select value={exportPreset} onValueChange={setExportPreset}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="pnl-export-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`pnl-export-range-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={exportPnL}
              disabled={exporting}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="pnl-export-button"
            >
              <DownloadSimple size={18} weight="duotone" className="mr-1.5" />
              {exporting ? "Exporting…" : "Export Excel"}
            </Button>
            <p className="text-[11px] text-slate-500 sm:ml-auto">
              On iPhone: Share → Save to Files → iCloud Drive
            </p>
          </div>
        </CardContent>
      </Card>

      {loading || !data ? (
        <div className="h-64 rounded-lg bg-slate-100 animate-pulse" />
      ) : (
        <>
          {/* The ledger */}
          <Card className="border-slate-200 overflow-hidden">
            <CardHeader className="pb-3 bg-gradient-to-r from-emerald-900 to-emerald-800 text-white">
              <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2 text-white">
                <Bank size={20} weight="duotone" />
                <span>P&amp;L Ledger Statement</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* REVENUE block */}
              <LedgerSection
                title="Revenue"
                accent="emerald"
                Icon={Plus}
                lines={(data.revenue_lines || []).map((r) => ({
                  label: r.label,
                  sub: `${r.count} sale${r.count !== 1 ? "s" : ""}`,
                  amount: r.net_sek,
                  sign: "+",
                }))}
                subtotal={{ label: "Net Revenue (Excl. Moms)", amount: data.net_revenue }}
                testId="ledger-revenue"
              />

              {/* EXPENDITURE block (NET of VAT — input Moms is reclaimable from Skatteverket) */}
              <LedgerSection
                title="Expenditure (Excl. Moms)"
                accent="red"
                Icon={Minus}
                lines={(data.expense_lines || []).map((e) => ({
                  label: e.label,
                  sub: `${e.count} expense${e.count !== 1 ? "s" : ""}${e.amount_sek !== e.amount_net_sek ? ` · gross ${formatSEK(e.amount_sek)}` : ""}`,
                  amount: e.amount_net_sek ?? e.amount_sek,
                  sign: "−",
                }))}
                subtotal={{ label: "Total Expenditure (Net of VAT)", amount: data.total_expenditure_net ?? data.total_expenditure, sign: "−" }}
                testId="ledger-expenditure"
              />

              {/* PROFIT BEFORE TAX */}
              <div className={`px-4 sm:px-6 py-4 ${data.is_loss ? "bg-red-50" : "bg-emerald-50"} border-t-2 ${data.is_loss ? "border-red-300" : "border-emerald-300"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Equals size={20} weight="bold" className={data.is_loss ? "text-red-700" : "text-emerald-800"} />
                    <div>
                      <div className="font-heading font-bold text-slate-900">Net Profit {data.is_loss ? "/ Loss " : ""}Before Tax</div>
                      <div className="text-[11px] text-slate-500">Net Revenue − Net Expenditure (both excl. VAT)</div>
                    </div>
                  </div>
                  <div
                    className={`font-heading font-extrabold text-2xl sm:text-3xl ${data.is_loss ? "text-red-700" : "text-emerald-800"}`}
                    data-testid="ledger-net-profit-before-tax"
                  >
                    {data.is_loss ? "−" : ""}{formatSEK(Math.abs(data.net_profit_before_tax))}
                  </div>
                </div>
              </div>

              {/* INCOME / CORPORATE TAX — entity-aware */}
              {!data.is_loss && entity === "AB" && (
                <LedgerSection
                  title="Corporate Tax (Bolagsskatt)"
                  accent="red"
                  Icon={Minus}
                  lines={[
                    { label: `Bolagsskatt @ ${((data.ab?.bolagsskatt_rate || 0.206) * 100).toFixed(1)}%`, sub: "Pre-Tax Accounting Profit × rate", amount: data.ab?.bolagsskatt ?? data.corporate_tax_20_6, sign: "−" },
                  ]}
                  testId="ledger-corp-tax"
                />
              )}
              {!data.is_loss && entity === "EF" && (
                <LedgerSection
                  title="Personal Income Tax (Enskild Firma)"
                  accent="red"
                  Icon={Minus}
                  lines={[
                    { label: `Egenavgifter @ ${((data.ef?.egenavgifter_rate || 0.2897) * 100).toFixed(2)}%`, sub: "Self-employed contributions", amount: data.ef?.egenavgifter || 0, sign: "−" },
                    { label: `Inkomstskatt @ ${((data.ef?.municipal_tax_rate || 0.32) * 100).toFixed(0)}%`, sub: "Applied to profit after egenavgifter", amount: data.ef?.inkomstskatt || 0, sign: "−" },
                  ]}
                  testId="ledger-personal-tax"
                />
              )}

              {/* NET PROFIT AFTER TAX — entity-aware */}
              <div className={`px-4 sm:px-6 py-5 ${data.is_loss ? "bg-slate-50" : "bg-gradient-to-r from-emerald-900 to-emerald-800 text-white"}`}>
                {data.is_loss ? (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-heading font-bold text-slate-900">
                        {entity === "EF" ? "No Income Tax Owed" : "No Corporate Tax Owed"}
                      </div>
                      <div className="text-[11px] text-slate-500">Net Loss for the period</div>
                    </div>
                    <div className="font-heading font-bold text-xl text-slate-700">0 kr</div>
                  </div>
                ) : entity === "AB" ? (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-200 font-bold">Final · After Bolagsskatt</div>
                      <div className="font-heading font-bold text-base sm:text-lg">Net Capital inside AB</div>
                    </div>
                    <div className="font-heading font-extrabold text-2xl sm:text-3xl" data-testid="ledger-net-profit-after-tax-ab">
                      {formatSEK(data.ab?.net_capital_inside_ab ?? data.net_profit_after_tax_20_6)}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-200 font-bold">Final · After Personal Tax</div>
                        <div className="font-heading font-bold text-base sm:text-lg">Personal Income After Tax</div>
                      </div>
                      <div className="font-heading font-extrabold text-2xl sm:text-3xl" data-testid="ledger-net-profit-after-tax-ef">
                        {formatSEK(data.ef?.personal_income_after_tax || 0)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-emerald-700/40">
                      <div className="text-[11px] text-emerald-100">Suggested tax reserve · 40–45 %</div>
                      <div className="font-heading font-semibold text-base text-emerald-100" data-testid="ledger-ef-reserve">
                        {formatSEK(data.ef?.tax_reserve_low || 0)} – {formatSEK(data.ef?.tax_reserve_high || 0)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* MEMO: Moms */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2 text-slate-700">
                <TrendDown size={18} weight="duotone" />
                Memo — Moms (collected for Skatteverket)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">
                Moms is collected from customers on behalf of Skatteverket and remitted separately. It is <span className="font-semibold">not</span> part of the P&amp;L above, shown here for reference.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <MemoRow label="Moms @ 6%" value={data.moms_6_owed} />
                <MemoRow label="Moms @ 25%" value={data.moms_25_owed} />
                <MemoRow label="Total Moms collected" value={data.moms_total_owed} bold accent="red" />
                <MemoRow label="Gross Revenue (Incl. Moms)" value={data.gross_revenue} bold accent="blue" />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

const LedgerSection = ({ title, accent, Icon, lines, subtotal, testId }) => {
  const isPlus = accent === "emerald";
  const headBg = isPlus ? "bg-emerald-50" : "bg-red-50";
  const headText = isPlus ? "text-emerald-900" : "text-red-900";
  const amountColor = isPlus ? "text-emerald-800" : "text-red-700";
  return (
    <div className="border-t border-slate-200" data-testid={testId}>
      <div className={`px-4 sm:px-6 py-2.5 ${headBg} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon size={14} weight="bold" className={headText} />
          <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${headText}`}>{title}</span>
        </div>
      </div>
      {lines.length === 0 ? (
        <div className="px-4 sm:px-6 py-3 text-sm text-slate-400 italic">No entries in this period</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {lines.map((l) => (
            <li key={`${l.label}-${l.sign}-${l.amount}`} className={`px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 ${l.muted ? "opacity-60" : ""}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{l.label}</div>
                {l.sub && <div className="text-[11px] text-slate-500">{l.sub}</div>}
              </div>
              <div className={`font-heading font-semibold text-sm sm:text-base shrink-0 ${l.sign === "+" ? "text-emerald-800" : amountColor}`}>
                {l.sign}{formatSEK(Math.abs(l.amount))}
              </div>
            </li>
          ))}
        </ul>
      )}
      {subtotal && (
        <div className={`px-4 sm:px-6 py-3 ${headBg} flex items-center justify-between gap-3 border-t border-slate-200`}>
          <div className="text-xs sm:text-sm font-bold uppercase tracking-wide text-slate-700">{subtotal.label}</div>
          <div className={`font-heading font-bold text-base sm:text-lg ${amountColor}`}>
            {subtotal.sign || ""}{formatSEK(Math.abs(subtotal.amount))}
          </div>
        </div>
      )}
    </div>
  );
};

const MemoRow = ({ label, value, bold, accent }) => {
  const accents = { red: "text-red-700", blue: "text-blue-700" };
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
      <span className="text-slate-600">{label}</span>
      <span className={`${bold ? "font-heading font-bold" : "font-medium"} ${accents[accent] || "text-slate-900"}`}>
        {formatSEK(value)}
      </span>
    </div>
  );
};
